import { useEffect, useState } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase, debugSession } from '@/integrations/supabase/client';
import { AuthContext } from '@/lib/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from "@/hooks/use-toast";
import { useInactivityTimeout } from '@/hooks/useInactivityTimeout';

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Initialize the inactivity timeout
  useInactivityTimeout();

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // First check if we're in a browser environment
        if (typeof window === 'undefined') {
          setLoading(false);
          return;
        }

        // Get the current session
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        
        if (currentSession) {
          if (mounted) {
            setSession(currentSession);
            setUser(currentSession.user);
          }
        }
      } catch (error: any) {
        console.error('Auth initialization error:', error);
        if (mounted) {
          toast({
            title: "Authentication Error",
            description: error.message || "Failed to initialize authentication",
            variant: "destructive"
          });
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        console.log('Auth state changed:', event, currentSession);
        
        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setLoading(false);

          // Handle navigation based on auth state
          if (event === 'SIGNED_OUT') {
            console.log('User signed out, redirecting to login');
            toast({
              title: "Signed Out",
              description: "You have been signed out successfully",
            });
            navigate('/auth/login');
          } else if (event === 'SIGNED_IN') {
            // If user was trying to access a protected route, redirect them there
            const from = location.state?.from || '/dashboard';
            console.log('User signed in, redirecting to:', from);
            toast({
              title: "Welcome Back",
              description: "You have been signed in successfully",
            });
            navigate(from, { replace: true });
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, location, toast]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, session }}>
      {children}
    </AuthContext.Provider>
  );
};
